import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import ModalLayout from "../../../components/structure/ModalLayout";
import ModalHeader from "../../../components/general/ModalHeader";
import FooterBar from "../../../components/structure/FooterBar";
import Button from "../../../components/general/Button";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import WebhookModel from "../../../models/webhooks/api/WebhookModel";
import { color_shades_dark, color_shades_darker, color_shades_light, color_text_default, color_text_light, silver_four } from "../../../constants/colors";
import { getConfigurationParameterNameFromOverrideKey, isConfigurationParameterOverrideKey, toConfigurationParameterOverrideKey, webhookConfigurationParameterOverridePrefix } from "../../../utils/webhookParameterOverrides";

interface WebhookOverridesModalProps {
    isVisible: boolean;
    onClose: () => void;
    webhooks: WebhookModel[];
    applicationId: string;
    currentOverrides: { [webhookId: string]: string };
    currentParamOverrides: { [webhookId: string]: { [paramName: string]: string } };
    onSave: (
        urlOverrides: { [webhookId: string]: string },
        paramOverrides: { [webhookId: string]: { [paramName: string]: string } }
    ) => void;
}

interface CustomParamOverrideRow {
    id: string;
    name: string;
    value: string;
}

const normalizeForComparison = (val: string | undefined | null) => {
    return (val ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .trim();
};

const WebhookOverridesModal: React.FC<WebhookOverridesModalProps> = (props) => {
    const [overrides, setOverrides] = useState<{ [webhookId: string]: string }>({});
    const [paramOverrides, setParamOverrides] = useState<{ [webhookId: string]: { [paramName: string]: string } }>({});
    const [customParamOverrides, setCustomParamOverrides] = useState<{ [webhookId: string]: CustomParamOverrideRow[] }>({});
    const [expandedWebhooks, setExpandedWebhooks] = useState<{ [webhookId: string]: boolean }>({});
    const customParamRowId = useRef(0);
    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION = 185;

    const createCustomParamRow = (webhookId: string, name = "", value = ""): CustomParamOverrideRow => {
        customParamRowId.current += 1;
        return {
            id: `${webhookId}-${customParamRowId.current}`,
            name,
            value
        };
    };

    const getConfiguredParamOverrideKeys = (webhook: WebhookModel) => {
        return new Set([
            ...(webhook.parameters ?? []).map(param => param.name),
            ...(webhook.configurationParameters ?? []).map(configParam => toConfigurationParameterOverrideKey(configParam.key))
        ]);
    };

    const getConfiguredParamNames = (webhook: WebhookModel) => {
        return new Set([
            ...(webhook.parameters ?? []).map(param => param.name.trim().toLowerCase()),
            ...(webhook.configurationParameters ?? []).map(configParam => configParam.key.trim().toLowerCase())
        ]);
    };

    useEffect(() => {
        if (props.isVisible) {
            const initial: { [webhookId: string]: string } = {};
            props.webhooks?.forEach(wh => {
                if (!wh.id) return;
                initial[wh.id] = props.currentOverrides[wh.id] ?? wh.url ?? "";
            });
            setOverrides(initial);
            const initialParams: { [webhookId: string]: { [paramName: string]: string } } = {};
            const initialCustomParams: { [webhookId: string]: CustomParamOverrideRow[] } = {};
            props.webhooks?.forEach(wh => {
                if (!wh.id) return;
                const webhookId = wh.id;
                const params: { [paramName: string]: string } = {};
                wh.parameters?.forEach(param => {
                    const currentValue = wh.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === param.name)?.value ?? param.defaultValue ?? "";
                    params[param.name] = props.currentParamOverrides?.[webhookId]?.[param.name] ?? currentValue;
                });
                wh.configurationParameters?.forEach(configParam => {
                    const overrideKey = toConfigurationParameterOverrideKey(configParam.key);
                    params[overrideKey] = props.currentParamOverrides?.[webhookId]?.[overrideKey] ?? configParam.value ?? "";
                });
                if (Object.keys(params).length > 0) {
                    initialParams[webhookId] = params;
                }
                const configuredParamOverrideKeys = getConfiguredParamOverrideKeys(wh);
                const currentWebhookParamOverrides = props.currentParamOverrides?.[webhookId] ?? {};
                const customParams = Object.keys(currentWebhookParamOverrides)
                    .filter(paramKey => !configuredParamOverrideKeys.has(paramKey))
                    .map(paramKey => createCustomParamRow(webhookId, getConfigurationParameterNameFromOverrideKey(paramKey), currentWebhookParamOverrides[paramKey] ?? ""));
                if (customParams.length > 0) {
                    initialCustomParams[webhookId] = customParams;
                }
            });
            setParamOverrides(initialParams);
            setCustomParamOverrides(initialCustomParams);
            setExpandedWebhooks({});
        }
    }, [props.isVisible, props.webhooks, props.currentOverrides, props.currentParamOverrides]);

    const handleOverrideChange = (webhookId: string, url: string) => {
        setOverrides(prev => ({ ...prev, [webhookId]: url }));
    };

    const handleParamOverrideChange = (webhookId: string, paramName: string, value: string) => {
        setParamOverrides(prev => ({
            ...prev,
            [webhookId]: {
                ...(prev[webhookId] ?? {}),
                [paramName]: value
            }
        }));
    };

    const handleAddCustomParam = (webhookId: string) => {
        setExpandedWebhooks(prev => ({ ...prev, [webhookId]: true }));
        setCustomParamOverrides(prev => ({
            ...prev,
            [webhookId]: [
                ...(prev[webhookId] ?? []),
                createCustomParamRow(webhookId)
            ]
        }));
    };

    const handleCustomParamNameChange = (webhookId: string, rowId: string, value: string) => {
        setCustomParamOverrides(prev => ({
            ...prev,
            [webhookId]: (prev[webhookId] ?? []).map(row => row.id === rowId ? { ...row, name: value } : row)
        }));
    };

    const handleCustomParamValueChange = (webhookId: string, rowId: string, value: string) => {
        setCustomParamOverrides(prev => ({
            ...prev,
            [webhookId]: (prev[webhookId] ?? []).map(row => row.id === rowId ? { ...row, value } : row)
        }));
    };

    const handleRemoveCustomParam = (webhookId: string, rowId: string) => {
        setCustomParamOverrides(prev => ({
            ...prev,
            [webhookId]: (prev[webhookId] ?? []).filter(row => row.id !== rowId)
        }));
    };

    const toggleExpand = (webhookId: string) => {
        setExpandedWebhooks(prev => ({ ...prev, [webhookId]: !prev[webhookId] }));
    };

    const handleClearAll = () => {
        const reset: { [webhookId: string]: string } = {};
        props.webhooks?.forEach(wh => {
            if (!wh.id) return;
            reset[wh.id] = wh.url ?? "";
        });
        setOverrides(reset);
        setParamOverrides({});
        setCustomParamOverrides({});
    };

    const getCustomParamNameError = (webhook: WebhookModel, row: CustomParamOverrideRow) => {
        const trimmedName = row.name.trim();
        const trimmedValue = row.value.trim();
        if (trimmedName.length === 0 && trimmedValue.length === 0) return "";
        if (trimmedName.length === 0) return "Enter a parameter name.";
        if (trimmedValue.length === 0) return "Enter a value.";
        if (isConfigurationParameterOverrideKey(trimmedName)) return `Parameter names cannot start with ${webhookConfigurationParameterOverridePrefix}.`;
        if (getConfiguredParamNames(webhook).has(trimmedName.toLowerCase())) return "This parameter already has a configured field.";

        const matchingCustomParams = (customParamOverrides[webhook.id ?? ""] ?? [])
            .filter(customRow => customRow.name.trim().toLowerCase() === trimmedName.toLowerCase());
        return matchingCustomParams.length > 1 ? "Custom parameter names must be unique." : "";
    };

    const hasCustomParamErrors = props.webhooks?.some(webhook => {
        return (customParamOverrides[webhook.id ?? ""] ?? []).some(row => getCustomParamNameError(webhook, row).length > 0);
    }) ?? false;

    const handleSave = () => {
        const effectiveUrlOverrides: { [webhookId: string]: string } = {};
        Object.keys(overrides).forEach(webhookId => {
            const url = overrides[webhookId];
            const webhook = props.webhooks?.find(wh => wh.id === webhookId);
            const trimmed = url?.trim() ?? "";
            if (trimmed.length > 0 && normalizeForComparison(trimmed) !== normalizeForComparison(webhook?.url)) {
                effectiveUrlOverrides[webhookId] = trimmed;
            }
        });

        const effectiveParamOverrides: { [webhookId: string]: { [paramName: string]: string } } = {};
        const webhookIds = Array.from(new Set([
            ...Object.keys(paramOverrides),
            ...Object.keys(customParamOverrides)
        ]));
        webhookIds.forEach(webhookId => {
            const webhook = props.webhooks?.find(wh => wh.id === webhookId);
            const overriddenParams: { [paramName: string]: string } = {};
            const params = paramOverrides[webhookId] ?? {};
            Object.keys(params ?? {}).forEach(paramKey => {
                const value = params[paramKey];
                const trimmed = value?.trim() ?? "";
                if (trimmed.length === 0) return;
                if (isConfigurationParameterOverrideKey(paramKey)) {
                    const configKey = getConfigurationParameterNameFromOverrideKey(paramKey);
                    const configParam = webhook?.configurationParameters?.find(cp => cp.key === configKey);
                    const currentValue = configParam?.value ?? "";
                    if (normalizeForComparison(trimmed) !== normalizeForComparison(currentValue)) {
                        overriddenParams[paramKey] = trimmed;
                    }
                } else {
                    const param = webhook?.parameters?.find(p => p.name === paramKey);
                    const currentValue = webhook?.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === paramKey)?.value ?? param?.defaultValue ?? "";
                    if (normalizeForComparison(trimmed) !== normalizeForComparison(currentValue)) {
                        overriddenParams[paramKey] = trimmed;
                    }
                }
            });
            (customParamOverrides[webhookId] ?? []).forEach(row => {
                const trimmedName = row.name.trim();
                const trimmedValue = row.value.trim();
                if (!webhook || trimmedName.length === 0 || trimmedValue.length === 0) return;
                if (getCustomParamNameError(webhook, row).length > 0) return;
                overriddenParams[trimmedName] = trimmedValue;
            });
            if (Object.keys(overriddenParams).length > 0) {
                effectiveParamOverrides[webhookId] = overriddenParams;
            }
        });

        props.onSave(effectiveUrlOverrides, effectiveParamOverrides);
        props.onClose();
    };

    const countOverriddenParams = (wh: WebhookModel) => {
        const webhookId = wh.id ?? "";
        const webhookParamOverrides = paramOverrides[webhookId] ?? {};
        const configuredParamOverrideCount = Object.keys(webhookParamOverrides).filter(paramKey => {
            const value = webhookParamOverrides[paramKey];
            const trimmed = value?.trim() ?? "";
            if (trimmed.length === 0) return false;
            if (isConfigurationParameterOverrideKey(paramKey)) {
                const configKey = getConfigurationParameterNameFromOverrideKey(paramKey);
                const configParam = wh.configurationParameters?.find(cp => cp.key === configKey);
                return normalizeForComparison(trimmed) !== normalizeForComparison(configParam?.value);
            } else {
                const param = wh.parameters?.find(p => p.name === paramKey);
                const currentValue = wh.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === paramKey)?.value ?? param?.defaultValue ?? "";
                return normalizeForComparison(trimmed) !== normalizeForComparison(currentValue);
            }
        }).length;
        const customParamOverrideCount = (customParamOverrides[webhookId] ?? []).filter(row => {
            return row.name.trim().length > 0 && row.value.trim().length > 0 && getCustomParamNameError(wh, row).length === 0;
        }).length;
        return configuredParamOverrideCount + customParamOverrideCount;
    };

    const activeOverrideCount = props.webhooks?.filter(wh => {
        if (!wh.id) return false;
        const urlVal = overrides[wh.id]?.trim() ?? "";
        const urlOverridden = urlVal.length > 0 && normalizeForComparison(urlVal) !== normalizeForComparison(wh.url);
        return urlOverridden || countOverriddenParams(wh) > 0;
    }).length ?? 0;

    return (
        <ModalLayout isVisible={props.isVisible} onClose={props.onClose}>
            <ModalHeader
                title="Webhook Overrides"
                onClose={props.onClose}
                rightText={activeOverrideCount > 0 ? `${activeOverrideCount} overridden` : undefined}
            />
            <CustomScrollbars
                autoHide
                autoHeight
                autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}
            >
                <Body>
                    <Description>
                        Override webhook URLs and parameters for simulator and test case requests.
                        These overrides are saved locally and will not affect live application behavior.
                    </Description>
                    {props.webhooks?.length > 0 ? (
                        <WebhookList>
                            {props.webhooks.filter(webhook => !!webhook.id).map(webhook => {
                                const webhookId = webhook.id as string;
                                const isUrlOverridden = (overrides[webhookId]?.trim() ?? "") !== "" && normalizeForComparison(overrides[webhookId]) !== normalizeForComparison(webhook.url);
                                const customParams = customParamOverrides[webhookId] ?? [];
                                const hasConfiguredParams = (webhook.parameters?.length ?? 0) > 0 || (webhook.configurationParameters?.length ?? 0) > 0;
                                const isExpanded = !!expandedWebhooks[webhookId];
                                const overriddenParamCount = countOverriddenParams(webhook);

                                return (
                                    <WebhookRow key={webhookId}>
                                        <WebhookHeader>
                                            <WebhookTitle>
                                                {webhook.title}
                                                {isUrlOverridden && <OverrideLabel>URL Overridden</OverrideLabel>}
                                                {overriddenParamCount > 0 && <OverrideLabel>{overriddenParamCount} param{overriddenParamCount > 1 ? "s" : ""} overridden</OverrideLabel>}
                                            </WebhookTitle>
                                            <ExpandButton onClick={() => toggleExpand(webhookId)} type="button">
                                                <ExpandChevron expanded={isExpanded}>▾</ExpandChevron>
                                                {isExpanded ? "Hide" : hasConfiguredParams || customParams.length > 0 ? "Show" : "Add"} params
                                            </ExpandButton>
                                        </WebhookHeader>
                                        <OverrideInput
                                            type="text"
                                            placeholder="Enter URL"
                                            value={overrides[webhookId] ?? ""}
                                            onChange={e => handleOverrideChange(webhookId, e.target.value)}
                                        />
                                        {isExpanded && (
                                            <ParamsSection>
                                                {(webhook.parameters?.length ?? 0) > 0 && (
                                                    <>
                                                        <ParamsSectionTitle>Instance Parameters</ParamsSectionTitle>
                                                        {(webhook.parameters ?? []).map(param => {
                                                            const currentValue = webhook.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === param.name)?.value ?? param.defaultValue ?? "";
                                                            const overrideValue = paramOverrides[webhookId]?.[param.name] ?? "";
                                                            const isParamOverridden = overrideValue.trim().length > 0 && normalizeForComparison(overrideValue) !== normalizeForComparison(currentValue);

                                                            return (
                                                                <ParamRow key={param.name}>
                                                                    <ParamLabel>
                                                                        {param.name}
                                                                        {param.isRequired && <RequiredMark>*</RequiredMark>}
                                                                        {isParamOverridden && <OverrideLabel>Overridden</OverrideLabel>}
                                                                    </ParamLabel>
                                                                    {param.description && <ParamDescription>{param.description}</ParamDescription>}
                                                                    <OverrideInput
                                                                        type="text"
                                                                        placeholder="Enter value"
                                                                        value={overrideValue}
                                                                        onChange={e => handleParamOverrideChange(webhookId, param.name, e.target.value)}
                                                                    />
                                                                </ParamRow>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                {(webhook.configurationParameters?.length ?? 0) > 0 && (
                                                    <>
                                                        <ParamsSectionTitle>Configuration Parameters</ParamsSectionTitle>
                                                        {(webhook.configurationParameters ?? []).map(configParam => {
                                                            const overrideKey = toConfigurationParameterOverrideKey(configParam.key);
                                                            const overrideValue = paramOverrides[webhookId]?.[overrideKey] ?? "";
                                                            const isParamOverridden = overrideValue.trim().length > 0 && normalizeForComparison(overrideValue) !== normalizeForComparison(configParam.value);
                                                            return (
                                                                <ParamRow key={configParam.key}>
                                                                    <ParamLabel>
                                                                        {configParam.key}
                                                                        {isParamOverridden && <OverrideLabel>Overridden</OverrideLabel>}
                                                                    </ParamLabel>
                                                                    {configParam.description && <ParamDescription>{configParam.description}</ParamDescription>}
                                                                    <OverrideInput
                                                                        type="text"
                                                                        placeholder="Enter value"
                                                                        value={overrideValue}
                                                                        onChange={e => handleParamOverrideChange(webhookId, overrideKey, e.target.value)}
                                                                    />
                                                                </ParamRow>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                <CustomParamsHeader>
                                                    <ParamsSectionTitle>Custom Parameters</ParamsSectionTitle>
                                                    <AddParamButton type="button" onClick={() => handleAddCustomParam(webhookId)}>
                                                        + Add Parameter
                                                    </AddParamButton>
                                                </CustomParamsHeader>
                                                {customParams.length > 0 ? customParams.map(row => {
                                                    const error = getCustomParamNameError(webhook, row);
                                                    return (
                                                        <CustomParamRow key={row.id}>
                                                            <CustomParamFields>
                                                                <OverrideInput
                                                                    type="text"
                                                                    placeholder="Parameter name"
                                                                    value={row.name}
                                                                    onChange={e => handleCustomParamNameChange(webhookId, row.id, e.target.value)}
                                                                />
                                                                <OverrideInput
                                                                    type="text"
                                                                    placeholder="Value"
                                                                    value={row.value}
                                                                    onChange={e => handleCustomParamValueChange(webhookId, row.id, e.target.value)}
                                                                />
                                                                <RemoveParamButton type="button" onClick={() => handleRemoveCustomParam(webhookId, row.id)}>
                                                                    Remove
                                                                </RemoveParamButton>
                                                            </CustomParamFields>
                                                            {error && <ParamError>{error}</ParamError>}
                                                        </CustomParamRow>
                                                    );
                                                }) : (
                                                    <ParamDescription>No custom parameters added.</ParamDescription>
                                                )}
                                            </ParamsSection>
                                        )}
                                    </WebhookRow>
                                );
                            })}
                        </WebhookList>
                    ) : (
                        <EmptyState>
                            No webhooks are configured for this application.
                        </EmptyState>
                    )}
                </Body>
            </CustomScrollbars>
            <FooterBar>
                <Button
                    type="button"
                    themes={["secondary", "start"]}
                    text="Clear All"
                    onClick={handleClearAll}
                    disabled={activeOverrideCount === 0}
                />
                <Button
                    type="button"
                    themes={["primary", "end"]}
                    text="Save"
                    onClick={handleSave}
                    disabled={hasCustomParamErrors}
                />
            </FooterBar>
        </ModalLayout>
    );
};

export default WebhookOverridesModal;

const Body = styled.div`
    padding: 24px 32px;
`;

const Description = styled.p`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    margin: 0 0 24px 0;
`;

const WebhookList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const WebhookRow = styled.div`
    border: 1px solid ${silver_four};
    border-radius: 8px;
    padding: 16px;
`;

const WebhookHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const WebhookTitle = styled.div`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${color_text_default};
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const OverrideLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    color: #e67e22;
    background: #fef5ec;
    border-radius: 4px;
    padding: 1px 6px;
`;

const ExpandButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Muli;
    font-size: 12px;
    color: ${color_text_light};
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    &:hover {
        background: ${color_shades_light};
        color: ${color_text_default};
    }
`;

const ExpandChevron = styled.span<{ expanded: boolean }>`
    display: inline-block;
    transition: transform 0.15s ease;
    transform: ${({ expanded }) => expanded ? "rotate(0deg)" : "rotate(-90deg)"};
    font-size: 14px;
    line-height: 1;
`;

const OverrideInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: #3498db;
    }
    &::placeholder {
        color: ${color_shades_darker};
        font-style: italic;
    }
`;

const ParamsSection = styled.div`
    margin-top: 12px;
    border-top: 1px solid ${color_shades_light};
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ParamsSectionTitle = styled.div`
    font-family: Muli;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${color_text_light};
`;

const CustomParamsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const AddParamButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: #007dbb;
    padding: 2px 0;
    white-space: nowrap;
`;

const ParamRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CustomParamRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CustomParamFields = styled.div`
    display: grid;
    grid-template-columns: minmax(130px, 1fr) minmax(180px, 2fr) auto;
    gap: 8px;
    align-items: center;
`;

const RemoveParamButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: #e74c3c;
    padding: 0 4px;
    white-space: nowrap;
`;

const ParamError = styled.div`
    font-family: Muli;
    font-size: 12px;
    color: #e74c3c;
`;

const ParamLabel = styled.div`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const RequiredMark = styled.span`
    color: #e74c3c;
    font-size: 13px;
`;

const ParamDescription = styled.div`
    font-family: Muli;
    font-size: 12px;
    color: ${color_text_light};
`;


const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    text-align: center;
    padding: 48px 0;
`;
